import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

type DeleteResult = { prUrl?: string; prNumber?: number } | void;

/**
 * Generic confirmation dialog for deleting a tree entity. The caller supplies an
 * async `action`; the dialog owns the busy/error lifecycle so the page doesn't
 * have to. KV deletes resolve to `void` and emit `deleted`; bundle deletes
 * resolve to a PR link, which is shown inline (the removal is PR-gated).
 */
@customElement("confirm-delete-dialog")
export class ConfirmDeleteDialog extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .body {
      font-size: 13px;
      color: var(--colors-foreground-fg-gray-900, #1d2433);
    }

    .name {
      font-family: monospace;
      font-weight: 600;
    }

    .hint {
      margin-top: var(--spacing-8, 8px);
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
    }

    .success-message {
      text-align: center;
      padding: var(--spacing-16, 16px) 0;
    }

    .modal-footer {
      display: flex;
      gap: var(--spacing-8, 8px);
      justify-content: flex-end;
    }

    bee-alert {
      margin-bottom: var(--spacing-16, 16px);
    }
  `;

  @property({ type: Boolean }) open = false;
  /** Human-readable entity label, e.g. "tenant", "instance", "PLM product", "bundle". */
  @property() entityType = "item";
  @property() name = "";
  /** Optional extra warning shown under the prompt (e.g. PR-based removal note). */
  @property() hint = "";
  /** The deletion to run on confirm. Set with a property binding (.action). */
  @property({ attribute: false }) action: () => Promise<DeleteResult> = async () => {};

  @state() private _busy = false;
  @state() private _error = "";
  @state() private _prUrl = "";
  @state() private _prNumber: number | null = null;

  willUpdate(changed: Map<string, unknown>) {
    // Reset on open, and also when the target identity changes while still open
    // (e.g. the same dialog instance is reused for a different node).
    const opened = changed.has("open") && this.open;
    const identityChanged = this.open && (changed.has("entityType") || changed.has("name"));
    if (opened || identityChanged) {
      this._busy = false;
      this._error = "";
      this._prUrl = "";
      this._prNumber = null;
    }
  }

  private _close() {
    this.dispatchEvent(new CustomEvent("close"));
  }

  private async _confirm() {
    if (this._busy) return;
    this._busy = true;
    this._error = "";
    try {
      const result = await this.action();
      if (result && result.prUrl) {
        // PR-gated removal (bundles): show the link instead of closing.
        this._prUrl = result.prUrl;
        this._prNumber = result.prNumber ?? null;
      } else {
        this.dispatchEvent(new CustomEvent("deleted", { detail: { name: this.name } }));
        this._close();
      }
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : `Failed to delete ${this.entityType}`;
    } finally {
      this._busy = false;
    }
  }

  render() {
    if (!this.open) return nothing;

    if (this._prUrl) {
      return html`
        <bee-modal open in-root title="Delete PR Created" @bee-request-close=${this._close}>
          <div class="success-message">
            <p>A pull request to remove this ${this.entityType} has been opened.</p>
            <p><bee-link href=${this._prUrl} target="_blank" rel="noopener noreferrer">Open PR${this._prNumber !== null ? ` #${this._prNumber}` : ""}</bee-link></p>
          </div>
          <div slot="footer" class="modal-footer">
            <bee-button label="Close" bee-type="secondary-grey" @click=${this._close}></bee-button>
          </div>
        </bee-modal>
      `;
    }

    return html`
      <bee-modal open in-root title="Delete ${this.entityType}" @bee-request-close=${this._close}>
        ${this._error ? html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>` : nothing}
        <div class="body">
          Delete ${this.entityType} <span class="name">${this.name}</span>? This action cannot be undone.
          ${this.hint ? html`<div class="hint">${this.hint}</div>` : nothing}
        </div>
        <div slot="footer" class="modal-footer">
          <bee-button label="Cancel" bee-type="secondary-grey" @click=${this._close} ?disabled=${this._busy}></bee-button>
          <bee-button
            label=${this._busy ? "Deleting..." : "Delete"}
            bee-type="primary-destructive"
            @click=${this._confirm}
            ?disabled=${this._busy}
            ?loading=${this._busy}
          ></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
